import React, { useEffect, useState, useRef } from 'react';
import { WEATHER_LEGEND_DATA } from '../home/WeatherMapControls';
import { useWeatherPointValue } from '../../../utils/api-hooks/weatherPoint';

/**
 * Surface treatments. `card` boxed the legend as a tile in the row that used to
 * sit between the map and the timeline; `overlay` is the current Global Produce
 * Map treatment — the legend floats ON the map, so the panel is translucent and
 * blurred to keep the tiles underneath readable; `strip` keeps the original
 * top-bordered band used by the home WeatherMap.
 */
const SURFACE_CLASS = {
  // The docked legend's own surface, from the design: translucent white over
  // a 10px blur rather than the timeline's flat fill. They sit side by side,
  // so this is a deliberate difference — the design treats the scale as glass
  // over the map and the timeline as a solid card.
  card: 'rounded-t-[16px] border border-white/70 bg-white/82 px-[11px] py-[6px] shadow-[0px_4px_18px_0px_rgba(17,24,39,0.14)] backdrop-blur-[10px] dark:border-white/10 dark:bg-[#1d1d1d]/82',
  overlay:
    'rounded-[10px] border border-white/70 bg-white/70 px-[12px] py-[7px] shadow-[0_2px_10px_rgba(0,0,0,0.12)] backdrop-blur-[6px] dark:border-white/10 dark:bg-[#1d1d1d]/70',
  strip: 'px-4 py-2 border-t border-[#E8EBE9] dark:border-[#3a3c3b]',
  // Phone treatment: the whole scale on one line, as a floating pill above the
  // timeline. The docked `card` is two rows with an axis, which at phone width
  // stretched into a full-bleed band — far too heavy for what is a readout.
  pill: 'rounded-[18px] border border-white/70 bg-white/85 px-[12px] py-[7px] shadow-[0_2px_10px_rgba(0,0,0,0.12)] backdrop-blur-[8px] dark:border-white/10 dark:bg-[#1d1d1d]/85',
};

/**
 * @param {'strip'|'card'|'overlay'|'pill'} variant see SURFACE_CLASS above.
 */
const WeatherLayerLegend = ({ activeLayer, isDark, variant = 'strip', center = null }) => {
  const [hoverInfo, setHoverInfo] = useState(null);
  const [tooltipLeft, setTooltipLeft] = useState(null);
  const barRef = useRef(null);
  const tooltipRef = useRef(null);
  const isOverlay = variant === 'overlay';
  // `card` is the docked variant beside the timeline, so it takes the compact
  // metrics too: the review asked for the same height as the timeline and
  // something less bulky than the full-size `strip`. Only `strip` — the
  // original row below the map, which has the room — stays large.
  const compact = isOverlay || variant === 'card';
  // `card` docks beside the timeline and takes the design's own metrics, which
  // are smaller again than the overlay's: 8.5px heading, 6px ramp, 8px ticks.
  const isDocked = variant === 'card';
  // Overlay sits on top of colourful weather tiles, so its text runs a shade
  // darker than the muted grey used on the plain white card surface.
  const headingClass = isDocked
    ? "font-['Inter'] text-[8.5px] font-extrabold uppercase tracking-[0.595px] text-[#6B7280] dark:text-[#9AA09A]"
    : compact
      ? 'text-[11px] leading-[16px] text-[#5D625F] dark:text-[#A8ADA9]'
      : 'text-xs text-[#828382]';
  const legendData = WEATHER_LEGEND_DATA[activeLayer];
  // Hooks must run unconditionally, so this sits above the early return.
  const pointValue = useWeatherPointValue(activeLayer, center);
  if (!legendData) return null;

  // Where the current value falls along the ramp, as a percentage. Clamped so
  // a reading outside the scale's range still marks its nearest end rather
  // than escaping the bar.
  const span = legendData.max - legendData.min;
  const markerPct = pointValue != null && span > 0
    ? Math.min(100, Math.max(0, ((pointValue - legendData.min) / span) * 100))
    : null;
  const readout = pointValue == null ? null
    : `${Math.round(pointValue)}${legendData.unit === '°C' ? '°' : ''}`;

  const stops = isDark
    ? (legendData.stopsDark || legendData.stops)
    : (legendData.stopsLight || legendData.stops);

  if (!stops || stops.length < 2) return null;

  const gradientStops = stops.map((stop, index) => {
    const percent = (index / (stops.length - 1)) * 100;
    return `${stop.color} ${percent}%`;
  }).join(', ');

  const getDisplayColor = (color) => {
    if (!color || color === 'transparent') return 'rgba(128,128,128,0.3)';
    if (color.startsWith('rgba') && color.includes(',0)')) return 'rgba(128,128,128,0.3)';
    return color;
  };

  const getDisplayStops = () => {
    if (stops.length <= 7) return stops.map((stop, i) => ({ ...stop, index: i }));
    const maxDisplay = 6;
    const step = (stops.length - 1) / (maxDisplay - 1);
    const indices = [];
    for (let i = 0; i < maxDisplay; i++) indices.push(Math.round(step * i));
    return [...new Set(indices)].map(i => ({ ...stops[i], index: i }));
  };

  const displayStops = getDisplayStops();

  const handleBarInteraction = (clientX) => {
    if (!barRef.current) return;
    const rect = barRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percent = Math.max(0, Math.min(1, x / rect.width));
    const stopIndex = Math.round(percent * (stops.length - 1));
    setHoverInfo({ x, stop: stops[stopIndex], percent });
  };

  // Clamp tooltip horizontal position so it doesn't overflow the bar container
  // (e.g. near the extreme left/right edge).
  useEffect(() => {
    if (!hoverInfo || !barRef.current || !tooltipRef.current) {
      setTooltipLeft(null);
      return;
    }
    const barRect = barRef.current.getBoundingClientRect();
    const tooltipWidth = tooltipRef.current.offsetWidth || 0;
    if (!barRect.width || tooltipWidth <= 0) {
      setTooltipLeft(null);
      return;
    }
    const half = tooltipWidth / 2;
    const clamped = Math.min(barRect.width - half, Math.max(half, hoverInfo.x));
    setTooltipLeft(clamped);
  }, [hoverInfo, activeLayer, isDark]);

  // One row: icon, unit, ramp, reading. No axis ticks and no heading word —
  // at phone width there is no room for them, and the reading on the right is
  // what the scale is actually consulted for.
  if (variant === 'pill') {
    return (
      <div className={`relative z-[1100] flex w-fit items-center gap-[8px] ${SURFACE_CLASS.pill}`}>
        <span
          aria-hidden="true"
          className="block size-[12px] shrink-0 bg-current text-[#5D625F] dark:text-[#9AA09A]"
          style={{
            maskImage: `url(/assets/icons/weather/${activeLayer}.svg)`,
            WebkitMaskImage: `url(/assets/icons/weather/${activeLayer}.svg)`,
            maskSize: '100% 100%',
            WebkitMaskSize: '100% 100%',
            maskRepeat: 'no-repeat',
            WebkitMaskRepeat: 'no-repeat',
          }}
        />
        <span className="font-['Inter'] text-[11px] font-semibold text-[#5D625F] dark:text-[#9AA09A]">
          {legendData.unit}
        </span>
        <span className="relative block h-[8px] w-[120px] shrink-0 rounded-[4px]"
          style={{
            background: `linear-gradient(to right, ${gradientStops})`,
            border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
          }}
        >
          {markerPct != null && (
            <span
              aria-hidden="true"
              className="pointer-events-none absolute top-[-2.5px] h-[13px] w-[4px] -translate-x-1/2 rounded-[2px] bg-[#1F2937] shadow-[0px_0px_0px_1.5px_rgba(255,255,255,0.85)] dark:bg-white dark:shadow-[0px_0px_0px_1.5px_rgba(0,0,0,0.55)]"
              style={{ left: `${markerPct}%` }}
            />
          )}
        </span>
        {readout && (
          <span className="font-['Inter'] text-[12px] font-extrabold text-[#111827] dark:text-[#E6E8E6]">
            {readout}
          </span>
        )}
      </div>
    );
  }

  return (
    // z-[1100] keeps the hover tooltip above Leaflet tile/overlay panes (~200–800)
    // when it extends upward over the map area below the legend bar.
    <div
      className={`relative z-[1100] overflow-visible ${
        variant === 'card' ? 'flex h-full flex-col justify-center' : ''
      } ${SURFACE_CLASS[variant] || SURFACE_CLASS.strip}`}
    >
      <div className={`flex items-center ${isDocked ? 'mb-[5px] gap-[5px]' : compact ? 'mb-1 gap-2' : 'mb-1.5 gap-2'}`}>
        {isDocked && (
          <span
            aria-hidden="true"
            className="block size-[11px] shrink-0 bg-current"
            style={{
              maskImage: `url(/assets/icons/weather/${activeLayer}.svg)`,
              WebkitMaskImage: `url(/assets/icons/weather/${activeLayer}.svg)`,
              maskSize: '100% 100%',
              WebkitMaskSize: '100% 100%',
              maskRepeat: 'no-repeat',
              WebkitMaskRepeat: 'no-repeat',
            }}
          />
        )}
        <span className={isDocked ? headingClass : `font-medium ${headingClass}`}>{legendData.label}</span>
        <span className={headingClass}>({legendData.unit})</span>
        {isDocked && readout && (
          <span className="ml-auto font-['Inter'] text-[11px] font-extrabold text-[#111827] dark:text-[#E6E8E6]">
            {readout}
          </span>
        )}
      </div>

      <div className="relative" style={{ minWidth: isDocked ? '180px' : isOverlay ? '160px' : '200px' }}>
        <div
          ref={barRef}
          className={`cursor-crosshair touch-none ${isDocked ? 'h-[6px] rounded-[3px]' : compact ? 'h-[10px] rounded' : 'h-3 rounded'}`}
          style={{
            background: `linear-gradient(to right, ${gradientStops})`,
            border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
          }}
          onMouseMove={(e) => handleBarInteraction(e.clientX)}
          onMouseLeave={() => setHoverInfo(null)}
          onTouchStart={(e) => e.touches.length > 0 && handleBarInteraction(e.touches[0].clientX)}
          onTouchMove={(e) => { if (e.touches.length > 0) { e.preventDefault(); handleBarInteraction(e.touches[0].clientX); } }}
          onTouchEnd={() => setHoverInfo(null)}
        />

        {/* Where the current reading falls on the ramp. `pointer-events-none`
            so it never swallows the hover that drives the tooltip above. */}
        {isDocked && markerPct != null && (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute top-[-2.5px] h-[11px] w-[4px] -translate-x-1/2 rounded-[2px] bg-[#1F2937] shadow-[0px_0px_0px_1.5px_rgba(255,255,255,0.85)] dark:bg-white dark:shadow-[0px_0px_0px_1.5px_rgba(0,0,0,0.55)]"
            style={{ left: `${markerPct}%` }}
          />
        )}

        {hoverInfo && hoverInfo.stop && (
          <div
            className="absolute bottom-full mb-2 z-50 pointer-events-none"
            style={{ left: tooltipLeft ?? hoverInfo.x, transform: 'translateX(-50%)' }}
          >
            <div
              ref={tooltipRef}
              className={`px-2 py-1.5 rounded text-xs whitespace-nowrap shadow-lg ${
              isDark ? 'bg-[#1d1d1d] text-white border border-[#333]' : 'bg-white text-[#282A29] border border-[#e5e7eb]'
              }`}
            >
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: getDisplayColor(hoverInfo.stop.color) }} />
                <div>
                  <div className="font-medium">{hoverInfo.stop.value} {legendData.unit}</div>
                  {hoverInfo.stop.label && <div className="text-[#828382] text-[0.625rem]">{hoverInfo.stop.label}</div>}
                </div>
              </div>
            </div>
            <div
              className="absolute left-1/2 -translate-x-1/2 w-0 h-0"
              style={{
                borderLeft: '6px solid transparent',
                borderRight: '6px solid transparent',
                borderTop: '6px solid',
                borderTopColor: isDark ? '#1d1d1d' : 'white',
              }}
            />
          </div>
        )}

        <div className={`relative mt-0.5 ${isDocked ? 'h-[12px]' : isOverlay ? 'h-4' : 'h-5'}`}>
          {displayStops.map((stop) => {
            const percent = (stop.index / (stops.length - 1)) * 100;
            return (
              <div
                key={stop.index}
                className="absolute flex flex-col items-center"
                style={{ left: `${percent}%`, transform: 'translateX(-50%)' }}
              >
                <div className="w-0.5 h-1.5" style={{ backgroundColor: getDisplayColor(stop.color) }} />
                <span className={`tabular-nums whitespace-nowrap ${isDocked ? "font-['Inter'] text-[8px] font-bold text-[#8A918B] dark:text-[#9AA09A]" : compact ? 'text-[0.625rem] text-[#5D625F] dark:text-[#A8ADA9]' : 'text-[0.625rem] text-[#828382]'}`}>{stop.value}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default WeatherLayerLegend;
